import { useParams } from 'react-router-dom';

import { LaunchSummary } from './components/LaunchSummary/LaunchSummary';
import { useLaunchChanges } from './useLaunchChanges';
import { useLaunchGuardView } from './useLaunchGuardView';
import { useLaunchSummaryActions } from './useLaunchSummaryActions';
import { useLaunchSummaryQueries } from './useLaunchSummaryQueries';

import type { CycleEvaluationGroupFormNavigatorParams } from '@/navigation/CycleEvaluation/CycleEvaluationGroupFormNavigator';

import {
    useApplyCycleEvaluationGroupChanges,
    useUpdateCycleEvaluationGroup,
} from '@/api/cycle-evaluation-services/cycle-evaluation-group/cycleEvaluationGroupMutations';
import { PerformanceProFeature } from '@/constants/PerformanceProFeature';
import { useAmelioTranslations, useNavigate } from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { useError } from '@/hooks/utils/useError';
import { requestPerformanceUpgrade } from '@/utils/requestPerformanceUpgrade';

export const LaunchSummaryPage = () => {
    const { t, i18n } = useAmelioTranslations();
    const { handleError } = useError();
    const navigate = useNavigate();
    const { cycleEvaluationGroupId } = useParams<CycleEvaluationGroupFormNavigatorParams>();

    const queries = useLaunchSummaryQueries(cycleEvaluationGroupId);

    const { mutateAsync: updateCycleEvaluationGroup, isPending: isUpdatePending } =
        useUpdateCycleEvaluationGroup();

    const { mutateAsync: applyCycleEvaluationGroupChanges, isPending: isApplyPending } =
        useApplyCycleEvaluationGroupChanges();

    const isLaunching = isUpdatePending || isApplyPending;

    const locale = i18n.language;

    const {
        evaluations,
        guardResult,
        scheduleRows,
        armedCount,
        periodLabel,
        isAlreadyLaunched,
        isManagerAssessmentReadOnly,
    } = useLaunchGuardView({
        group: queries.group,
        orphanMilestones: queries.orphanMilestones,
        checkedTypes: queries.checkedTypes,
        cycleEvaluationByType: queries.cycleEvaluationByType,
        hasQuestionnaireContentByType: queries.hasQuestionnaireContentByType,
        locale,
        t,
    });

    const changes = useLaunchChanges({
        group: queries.group,
        orphanMilestones: queries.orphanMilestones,
        checkedTypes: queries.checkedTypes,
        cycleEvaluationByType: queries.cycleEvaluationByType,
        evaluations,
        locale,
        t,
    });

    // An absent list means the cycle carries no launch snapshot to compare against, which is read
    // as "nothing to apply" rather than "anything may be applied".
    const hasChangesToApply = (changes?.length ?? 0) > 0;

    const { onBack, onLaunch } = useLaunchSummaryActions({
        group: queries.group,
        cycleEvaluationGroupId,
        updateCycleEvaluationGroup,
        applyCycleEvaluationGroupChanges,
        isLaunchBlocked: guardResult.isBlocked,
        isAlreadyLaunched,
        hasChangesToApply,
        armedCount,
        t,
        navigate,
        handleError,
    });

    useFetchErrorsHandler({
        errors: [
            queries.isGroupError,
            queries.isMilestonesError,
            queries.isCompanyInformationError,
            queries.isCompanyPeopleError,
            queries.isPermissionsError,
            queries.isCycleEvaluationError,
        ],
    });

    return (
        <LaunchSummary
            isLoading={queries.isLoading}
            cycleName={queries.group?.name ?? ''}
            periodLabel={periodLabel}
            resolvedEmployeeCount={queries.resolvedEmployeeCount}
            evaluations={evaluations}
            schedule={scheduleRows}
            isLaunching={isLaunching}
            isLaunchBlocked={guardResult.isBlocked}
            isConfigurationLocked={guardResult.isConfigurationLocked}
            isAlreadyLaunched={isAlreadyLaunched}
            changes={changes}
            hasChangesToApply={hasChangesToApply}
            launchNote={t('cycleEvaluationSchedule.launchSummary.launchReview.note')}
            isLaunchLockedByPlan={isManagerAssessmentReadOnly}
            onBack={onBack}
            onLaunch={onLaunch}
            onLockedLaunch={() =>
                requestPerformanceUpgrade(PerformanceProFeature.ManagerAssessment)
            }
        />
    );
};
